'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { AUTH_REFRESH_BUFFER_MS, AUTH_SESSION_EXPIRY_COOKIE_KEY } from '@workspace/ui/lib';
import { useEffect, useRef } from 'react';

const ACTIVITY_THROTTLE_RATIO = 0.025;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
const SESSION_REFRESH_RETRY_MS = 5_000;
const MISSING_METADATA_MAX_ATTEMPTS = 3;

interface SessionExpiryMetadata {
  issuedAt: number;
  expiresAt: number;
}

interface UseSessionExtensionOptions {
  isEnabled: boolean;
  sessionVersion: string | null;
  refreshSession: () => Promise<{ success: boolean }>;
}

function readCookieValue(name: string): string | null {
  const cookie = document.cookie
    .split('; ')
    .find((cookiePart) => cookiePart.startsWith(`${name}=`));

  if (!cookie) return null;
  return decodeURIComponent(cookie.slice(name.length + 1));
}

function readSessionExpiryMetadata(): SessionExpiryMetadata | null {
  const value = readCookieValue(AUTH_SESSION_EXPIRY_COOKIE_KEY);
  if (!value) return null;

  const [issuedAtValue, expiresAtValue] = value.split(':');
  const issuedAt = Number(issuedAtValue);
  const expiresAt = Number(expiresAtValue);

  if (!Number.isFinite(issuedAt) || !Number.isFinite(expiresAt) || expiresAt <= issuedAt)
    return null;

  return { issuedAt, expiresAt };
}

export function useSessionExtension({
  isEnabled,
  sessionVersion,
  refreshSession,
}: UseSessionExtensionOptions): void {
  const lastActivityAtRef = useRef<number | null>(null);
  const lastRecordedAtRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const hasAttemptedRefreshRef = useRef(false);

  useEffect(() => {
    // A new access token means a new refresh-token window may have started.
    void sessionVersion;

    if (!isEnabled) return;

    const metadata = readSessionExpiryMetadata();
    if (!metadata) {
      let retryTimeoutId: number | undefined;
      let attemptCount = 0;

      const attemptRefresh = () => {
        if (isRefreshingRef.current || attemptCount >= MISSING_METADATA_MAX_ATTEMPTS) return;

        attemptCount += 1;
        isRefreshingRef.current = true;
        void refreshSession()
          .then((result) => {
            if (!result.success && attemptCount < MISSING_METADATA_MAX_ATTEMPTS) {
              retryTimeoutId = window.setTimeout(attemptRefresh, SESSION_REFRESH_RETRY_MS);
            }
          })
          .catch(() => {
            if (attemptCount < MISSING_METADATA_MAX_ATTEMPTS) {
              retryTimeoutId = window.setTimeout(attemptRefresh, SESSION_REFRESH_RETRY_MS);
            }
          })
          .finally(() => {
            isRefreshingRef.current = false;
          });
      };

      attemptRefresh();
      return () => window.clearTimeout(retryTimeoutId);
    }

    const refreshLifetimeMs = metadata.expiresAt - metadata.issuedAt;
    const activityThrottleMs = refreshLifetimeMs * ACTIVITY_THROTTLE_RATIO;
    const refreshAt = Math.max(metadata.expiresAt - AUTH_REFRESH_BUFFER_MS, metadata.issuedAt);

    lastActivityAtRef.current = null;
    lastRecordedAtRef.current = 0;
    hasAttemptedRefreshRef.current = false;
    let retryTimeoutId: number | undefined;

    const checkSessionExtension = () => {
      const now = Date.now();
      const hasActivitySinceRefresh =
        lastActivityAtRef.current !== null && lastActivityAtRef.current > metadata.issuedAt;

      if (
        now < refreshAt ||
        now >= metadata.expiresAt ||
        !hasActivitySinceRefresh ||
        isRefreshingRef.current ||
        hasAttemptedRefreshRef.current
      )
        return;

      isRefreshingRef.current = true;
      hasAttemptedRefreshRef.current = true;

      void refreshSession()
        .then((result) => {
          if (result.success) return;

          hasAttemptedRefreshRef.current = false;
          if (Date.now() < metadata.expiresAt) {
            retryTimeoutId = window.setTimeout(
              checkSessionExtension,
              Math.min(SESSION_REFRESH_RETRY_MS, metadata.expiresAt - Date.now())
            );
          }
        })
        .catch(() => {
          hasAttemptedRefreshRef.current = false;
          if (Date.now() < metadata.expiresAt) {
            retryTimeoutId = window.setTimeout(
              checkSessionExtension,
              Math.min(SESSION_REFRESH_RETRY_MS, metadata.expiresAt - Date.now())
            );
          }
        })
        .finally(() => {
          isRefreshingRef.current = false;
        });
    };

    const markActivity = () => {
      const now = Date.now();
      if (now - lastRecordedAtRef.current < activityThrottleMs) return;

      lastRecordedAtRef.current = now;
      lastActivityAtRef.current = now;
      checkSessionExtension();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') markActivity();
    };

    const handleOnline = () => {
      checkSessionExtension();
    };

    const handleWindowBlur = () => {
      window.setTimeout(() => {
        if (
          document.visibilityState === 'visible' &&
          document.activeElement instanceof HTMLIFrameElement
        )
          markActivity();
      }, 0);
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, markActivity, { passive: true });
    }
    window.addEventListener('focus', markActivity);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const timeoutId = window.setTimeout(checkSessionExtension, Math.max(refreshAt - Date.now(), 0));

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, markActivity);
      }
      window.removeEventListener('focus', markActivity);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearTimeout(timeoutId);
      window.clearTimeout(retryTimeoutId);
    };
  }, [isEnabled, refreshSession, sessionVersion]);
}
